import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Paywall from "@/components/Paywall";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Subscription from "@/pages/Subscription";
import SuperAdmin from "@/pages/SuperAdmin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import CalendarPage from "@/pages/CalendarPage";
import Jobs from "@/pages/Jobs";
import WorkOrder from "@/pages/WorkOrder";
import Absences from "@/pages/Absences";
import Approvals from "@/pages/Approvals";
import Staff from "@/pages/Staff";
import Reports from "@/pages/Reports";
import PriceList from "@/pages/PriceList";
import Showcase from "@/pages/Showcase";
import Installation from "@/pages/Installation";
import Settings from "@/pages/Settings";
import ErrorBoundary from "@/components/ErrorBoundary";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-slate-500">Laddar…</div>;
  if (!user) return <Navigate to="/logga-in" replace />;
  return children;
}

// Affärsvyer kräver aktivt abonnemang (superadmin och gratis åtkomst passerar alltid)
function Licensed({ children }) {
  const { company, companyLoading } = useAuth();
  if (companyLoading) return <div className="p-8 text-sm text-slate-500">Laddar…</div>;
  if (company && company.has_access === false) return <Paywall company={company} />;
  return children;
}

// Superadmin är en intern funktion för systemägaren – dold och blockerad för alla andra
function SuperOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "superadmin") return <Navigate to="/kalender" replace />;
  return children;
}

const licensed = (el) => <Licensed>{el}</Licensed>;

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <ErrorBoundary>
          <Routes>
            <Route path="/logga-in" element={<Login />} />
            <Route path="/skapa-konto" element={<Register />} />
            <Route path="/registrera" element={<Navigate to="/skapa-konto" replace />} />
            <Route path="/demo" element={<Showcase />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route path="/" element={<Navigate to="/kalender" replace />} />
              <Route path="/installningar" element={<Settings />} />
            <Route path="/abonnemang" element={<Subscription />} />
              <Route
                path="/superadmin"
                element={
                  <SuperOnly>
                    <SuperAdmin />
                  </SuperOnly>
                }
              />
              <Route path="/kalender" element={licensed(<CalendarPage />)} />
              <Route path="/jobb" element={licensed(<Jobs />)} />
              <Route path="/nymontering/:id" element={<Installation />} />
            <Route path="/arbetsorder/:id" element={licensed(<WorkOrder />)} />
              <Route path="/franvaro" element={licensed(<Absences />)} />
              <Route path="/godkannande" element={licensed(<Approvals />)} />
              <Route path="/personal" element={licensed(<Staff />)} />
              <Route path="/tidrapport" element={licensed(<Reports />)} />
              <Route path="/prislista" element={licensed(<PriceList />)} />
            </Route>
            <Route path="*" element={<Navigate to="/kalender" replace />} />
          </Routes>
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
